import { createFileRoute } from "@tanstack/react-router"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { api, type ApiEnvelope, type ContactMessage } from "@/lib/api"
import { getErrorMessage } from "@/lib/get-error-message"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { ConfirmDelete } from "@/components/admin/confirm-delete"

export const Route = createFileRoute("/admin/_authed/messages")({
  component: MessagesPage,
})

function MessagesPage() {
  const queryClient = useQueryClient()
  const { data, isLoading, isError } = useQuery<ContactMessage[]>({
    queryKey: ["admin", "contact-messages"],
    queryFn: async () => (await api.get<ApiEnvelope<ContactMessage[]>>("/admin/contact-messages")).data.data,
  })

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["admin", "contact-messages"] })
    queryClient.invalidateQueries({ queryKey: ["admin", "dashboard"] })
  }

  const toggleRead = useMutation({
    mutationFn: async (msg: ContactMessage) => api.patch(`/admin/contact-messages/${msg.id}`, { is_read: !msg.is_read }),
    onSuccess: (_data, msg) => {
      invalidate()
      toast.success(msg.is_read ? "Marked as unread." : "Marked as read.")
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const remove = useMutation({
    mutationFn: async (id: number) => api.delete(`/admin/contact-messages/${id}`),
    onSuccess: () => {
      invalidate()
      toast.success("Message deleted.")
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  return (
    <div>
      <h1 className="font-display text-3xl font-bold">Messages</h1>
      <p className="mt-1 text-sm text-muted-foreground">Submissions from the public contact form.</p>

      <div className="mt-8 rounded-2xl border border-border bg-card">
        {isLoading ? (
          <div className="h-40 animate-pulse rounded-2xl bg-secondary" />
        ) : isError ? (
          <p className="p-6 text-sm text-destructive">Couldn't load messages.</p>
        ) : !data || data.length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground">No messages yet.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>From</TableHead>
                <TableHead>Subject</TableHead>
                <TableHead>Received</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((msg) => (
                <TableRow key={msg.id} className={!msg.is_read ? "bg-accent/10 font-medium" : undefined}>
                  <TableCell>
                    <p>{msg.name}</p>
                    <p className="text-xs text-muted-foreground">{msg.email}</p>
                  </TableCell>
                  <TableCell>
                    <p>{msg.subject}</p>
                    <p className="max-w-xs truncate text-xs text-muted-foreground">{msg.message}</p>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{new Date(msg.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Badge variant={msg.is_read ? "secondary" : "default"}>{msg.is_read ? "Read" : "Unread"}</Badge>
                  </TableCell>
                  <TableCell className="flex justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={() => toggleRead.mutate(msg)}>
                      Mark {msg.is_read ? "unread" : "read"}
                    </Button>
                    <ConfirmDelete itemLabel={`message from ${msg.name}`} onConfirm={() => remove.mutate(msg.id)} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  )
}
